
/**
 * The promote routine of an ephemeral sheet (`LonaSdk.Client.openEphemeral`):
 * what runs on the sheet's first persisting edit. It creates the sheet on the
 * server — or resumes the create an earlier attempt committed — with its root
 * rows, reconciles every nested row (`PromotionWrites`), refuses to call the
 * sheet promoted until every local row has its own server row in its place
 * (`PromoteIncompleteError`), and then re-keys the open core in place
 * (`SheetHandleCore.Promotable.applyPromotion`) and announces it
 * (`ClientConfig.onSheetPromoted`). The client hands it the few seams it
 * needs (`EphemeralPromotion.Seams`); the correlations a committed create
 * confirmed live in the routine, so a retry resumes instead of re-creating.
 */
import type { Option } from "@tento-lona/sheets";
import type { Rational } from "@tento-core/rational";
import { Tracing } from "@tento-core/tracing";
import { type RowId, type RowLocalId, RowNetworkKey, SheetId } from "@tento-lona/sheets";
import { elementAt } from "@tento-lona/sheets/internal-sdk";
import { Network } from "../../network.ts";
import { PromoteIncompleteError } from "../../errors.ts";
import type { PromotionCommit } from "../promotion-commit.ts";
import type { SheetHandleCore } from "../sheet-handle-core.ts";
import {
  correlateRecoveredRows,
  type PromotedSheet,
  promoteServerTree,
  type PromotionNewUserSheet,
  promotionNewRow,
  promotionRowKey,
  templateSynthesizedRowKeys,
} from "./promotion-rows.ts";
import type { PromotionWrites } from "./promotion-writes.ts";

/**
 * What a promote announces: the sheet's new server id beside the local id it
 * replaced, with its title and lookup key. Delivered to
 * `ClientConfig.onSheetPromoted`, the one place an app learns that a sheet
 * was promoted (a callback payload, not an `Invalidation`).
 */
export type SheetPromoted = {
  readonly sheetId: SheetId;
  readonly previousSheetId: SheetId;
  readonly title: Option<string>;
  readonly lookupKey: Option<string>;
  readonly rowsSent: number;
  readonly rowsRekeyed: number;
  /**
   * What the promotion did, typed: which sheet, and which row identities
   * survived the re-key. (The `SheetListChanged` a promote emits carries
   * `SheetsClient.PROMOTE_ACTOR` only so a host can skip its own echo; it
   * is not how a promotion is recognised.)
   */
  readonly commit: PromotionCommit;
};

export namespace EphemeralPromotion {
  /** What the routine reaches of the client that opened the sheet. */
  export interface Seams {
    /** The client's row ids: a serialized row's raw id resolves through them. */
    readonly ids: RowLocalId.Resolver;
    /** The promotion's requests, over the client's API and sheets client. */
    readonly writes: () => PromotionWrites;
    /** `ClientConfig.onSheetPromoted`; a throw from it is traced, not raised. */
    readonly onSheetPromoted: Option<(event: SheetPromoted) => void>;
    /**
     * The sheet is promoted (its promoter already cleared): move the client's
     * open-sheet cache from `oldId` to the sheet's server id, and to its
     * lookup key (`null`: none).
     */
    readonly rehome: (oldId: string, lookupKey: Option<string>) => void;
  }
}

/**
 * The promote routine for an ephemeral sheet, over the seam its core built
 * (`sheet`), to install with
 * `SheetHandleCore.installPromoter`. Each call is one attempt; a failed
 * attempt leaves the core local and the next call resumes it.
 */
export function ephemeralPromoter(
  sheet: SheetHandleCore.Promotable,
  seams: EphemeralPromotion.Seams,
): () => Promise<void> {
  // The row correlations (local key -> server key) a promote attempt has
  // CONFIRMED — the create's, then each hydrate's. Kept across attempts so
  // a retry after a partial failure resumes instead of re-creating; `null`
  // until a create has committed.
  let confirmed: Option<Map<string, string>> = null;
  return async () => {
    const oldId = sheet.network.id;
    const serialized = sheet.serialize();
    // `serialize()` excludes synthetic rows but emits them in `allRows()`
    // insertion order, NOT display order — so the position must come from
    // the ordered tree (`rowPositionsByRawId`), keyed by the same `rawId`
    // serialize stamps. Sending each row's REAL position keeps the promoted
    // sheet's order identical to what the user saw; the server preserves the
    // sent position and `parse` re-sorts by it, so send order is moot.
    // `type` is the wire type (e.g. `alias.calendar`), not the retired
    // descriptor id (`calendar`) that would 422; the reserved `lookupKey`
    // rides across so canonical rows (`:~lona:tasks`) reuse server-side.
    //
    // Row IDENTITY stays server-authoritative: we send NO `clientId`, so
    // `create_with_rows` mints a fresh server uuid per row. A client-minted
    // id would land in the server's GLOBAL row namespace (the
    // `spreadsheet_rows` PK is the id alone, and `add_row` adopts an
    // existing id rather than erroring), so deterministic ephemeral ids
    // (`fakeUserRowId` = hex of the row name; hardcoded raw-template uuids)
    // could silently share a physical row across sheets/users. Instead the
    // reused ephemeral rows-side `Sheet` adopts the server ids after the
    // fact via `applyPromotion` → `modelCache.rekeyRows`, keyed by the
    // `idRemap` correlated below.
    const posByRawId = sheet.rowPositions();
    const positionOf = (id: RowId): Option<Rational> =>
      posByRawId.get(seams.ids.resolveFromRawId(String(id)).rawId) ?? null;
    // Template-synthesized rows (`virtual(r_<alias>|calendar-body)` and
    // its `alias.calendar-list`) are not persisted at all — the next load
    // re-synthesizes them under the alias's SERVER id, and a persisted
    // copy (fresh `r_` id) would load beside them as a duplicate. See
    // `templateSynthesizedRowKeys`.
    const template = templateSynthesizedRowKeys(serialized.rows);
    const persistable = serialized.rows.filter((rr) =>
      !template.skipped.has(promotionRowKey(rr.id))
    );
    // `create_with_rows` is FLAT (no parent/children on the wire), so it
    // carries only the ROOTS. Every real nested row — the header
    // surface's default-weather, a detached alias's children — is
    // persisted under its parent's server id by `reconcilePromotedRows`
    // below. Sending them here instead stored them as position-less
    // top-level rows (the flattened-calendar bug). Exception: a nested
    // row with a lookup key still rides the create so the server reuses
    // the user's canonical row (`fork_if_neccesary`); it is re-parented
    // afterwards.
    const parentOf = new Map<string, string>();
    for (const rr of persistable) {
      for (const child of rr.children ?? []) {
        const childKey = promotionRowKey(child);
        if (template.skipped.has(childKey)) continue;
        parentOf.set(childKey, promotionRowKey(rr.id));
      }
    }
    const createdRows = persistable.filter((rr) =>
      !parentOf.has(promotionRowKey(rr.id)) || rr.lookupKey != null
    );
    const newRows: Network.NewRowWithAttributes[] = createdRows.map(
      (rr) => promotionNewRow(rr, positionOf(rr.id) ?? rr.position),
    );
    const newSheet: PromotionNewUserSheet = {
      ...Network.NewUserSheet.make({
      // Pass the ephemeral's client-minted UUID so the server
      // preserves it. `sheet.id` is the canonical SheetId string
      // (`ss_<hex>`), but `NewUserSheet.id` is a strict
      // `uuid::Uuid` (lona-models/src/sheet/new_sheet.rs) that
      // rejects the `ss_` prefix → 422 on the whole create. Send
      // the bare UUID form so it deserializes; the server re-emits
      // it (canonicalized back to `ss_<hex>` on the client), so
      // identity still round-trips.
      id: sheet.id.toUuid() ?? sheet.network.id,
      title: sheet.title,
      description: serialized.description,
      width: serialized.width,
      time_scale: serialized.timeScale,
      lookup_key: serialized.lookupKey,
      rows: newRows.length > 0 ? newRows : null,
        position: null,
      }),
      ...(serialized.timeScale != null ? { timeScale: serialized.timeScale } : {}),
      ...(serialized.lookupKey != null ? { lookupKey: serialized.lookupKey } : {}),
    };
    // A retry after a failed attempt RESUMES: that attempt's create
    // committed (its correlations are in `confirmed`), so read the sheet
    // as the server holds it now and reconcile the rest against it. A
    // sheet that is gone falls back to a fresh create.
    const resumed = confirmed == null
      ? null
      : (await seams.writes().loadExisting(SheetId.of(oldId))).sheet;
    const promoted: PromotedSheet = resumed != null
      ? { sheet: resumed, adopted: true }
      : await seams.writes().createOrLoad(newSheet, oldId);
    const sheetKey = SheetId.of(String(promoted.sheet.id)).toString();
    const serverIdOf = new Map<string, string>();
    const responseRows = promoted.sheet.rows ?? [];
    const server = promoteServerTree(responseRows);
    let rootsMatched = 0;
    let rootsUnmatched = 0;
    if (!promoted.adopted) {
      // A FRESH create (`isFreshCreateResponse` checked it index by index):
      // `create_with_rows` pushes one result row per input row in order, so
      // `responseRows[i]` is the server row for `createdRows[i]` —
      // including the canonical lookup-key fork row, which lands a server
      // id distinct from the sent one. Both ids are canonicalized to the
      // rows-side raw-id form (what `serialize` emits and the ordered tree
      // interns), so `rekeyRows` matches on both sides.
      for (let i = 0; i < createdRows.length; i++) {
        serverIdOf.set(
          promotionRowKey(elementAt(createdRows, i, "promote: created row").id),
          promotionRowKey(elementAt(responseRows, i, "promote: response row").id),
        );
      }
      rootsMatched = createdRows.length;
    } else {
      // The sheet was already persisted — by this handle's own failed
      // attempt (`resumed`), by an earlier promote of the same id handed
      // back whole by the idempotent create, or read after a legacy
      // duplicate-key error. Its rows are the whole stored sheet in no
      // order: correlate structurally.
      if (resumed != null && confirmed != null) {
        for (const [localKey, serverKey] of confirmed) {
          if (server.rows.has(serverKey)) serverIdOf.set(localKey, serverKey);
        }
      }
      const roots = correlateRecoveredRows(
        createdRows,
        newRows.map((nr) => nr.position ?? null),
        server,
        serverIdOf,
      );
      rootsMatched = roots.matched;
      rootsUnmatched = roots.unmatched;
    }
    // The create is committed: from here a failure leaves a partial sheet,
    // and the retry resumes from these correlations (extended in place by
    // every confirmed write).
    confirmed = serverIdOf;
    const reconciled = await seams.writes().reconcileRows(
      sheetKey,
      persistable,
      parentOf,
      createdRows.map((row, i) => ({ row, sent: elementAt(newRows, i, "promote: sent row") })),
      serverIdOf,
      server,
    );
    const serverRows = reconciled.rows;
    if (promoted.adopted) {
      // `rootsUnmatched`: roots the stored sheet lacked (re-added:
      // `rowsAdded`); `alreadyPersisted`: nested rows an earlier attempt
      // stored (not rewritten); `readded`: nested rows re-created under an
      // already-hydrated parent; `unresolved`: nested rows whose parent has
      // no server row (the promote then fails incomplete).
      Tracing.log("cow-promote-resume", {
        sheetId: SheetId.of(oldId).toString(),
        source: resumed != null ? "retry" : "adopted",
        serverRows: responseRows.length,
        rootsMatched,
        rootsUnmatched,
        rowsAdded: reconciled.rowsAdded,
        parents: reconciled.parents,
        alreadyPersisted: reconciled.alreadyPersisted,
        children: reconciled.hydrated,
        readded: reconciled.readded,
        reparented: reconciled.reparented,
        patched: reconciled.patched,
        unresolved: reconciled.unresolved,
        templateSkipped: template.skipped.size,
      });
    } else if (
      parentOf.size > 0 || template.skipped.size > 0 || template.kept > 0
    ) {
      // `templateSkipped`: synthesized rows left to the next load's
      // template expansion; `templateKept`: virtual rows persisted anyway
      // because they hold a real descendant (each will load beside its
      // re-synthesized twin — nonzero is worth a look).
      Tracing.log("cow-promote-hydrate", {
        sheetId: SheetId.of(oldId).toString(),
        parents: reconciled.parents,
        children: reconciled.hydrated,
        reparented: reconciled.reparented,
        patched: reconciled.patched,
        templateSkipped: template.skipped.size,
        templateKept: template.kept,
      });
    }
    // COMPLETE correspondence, or the handle stays local: every local row
    // the promote had to save has its own server row. Never a synced handle
    // over an unsaved row.
    const missing: string[] = [];
    const mapped = new Set<string>();
    for (const rr of persistable) {
      const localKey = promotionRowKey(rr.id);
      const serverKey = serverIdOf.get(localKey) ?? null;
      if (serverKey == null || mapped.has(serverKey)) missing.push(localKey);
      else mapped.add(serverKey);
    }
    // ...and sits where the local tree has it, with its metadata: a
    // retained id is not a saved edge (`reconcilePromotedRows`).
    const misplaced = reconciled.misplaced.filter((key) =>
      !missing.includes(key)
    );
    if (missing.length > 0 || misplaced.length > 0) {
      throw new PromoteIncompleteError(
        SheetId.of(oldId),
        [...missing, ...misplaced].map(RowNetworkKey.fromWire),
        persistable.length,
        misplaced.map(RowNetworkKey.fromWire),
      );
    }
    // The server sheet as persisted: the created roots plus every nested
    // row, each parent carrying its server `children`. (`applyPromotion`
    // takes only its metadata — the handle keeps its tree and re-keys in
    // place — so this is not re-fetched.)
    const serverSheet: Network.UserSheetWithRows = {
      ...promoted.sheet,
      rows: serverRows,
    };
    // Re-key in serialized (display, depth-first) order; for a flat sheet
    // that is exactly the create's send order.
    const idRemap = new Map<string, string>();
    for (const rr of serialized.rows) {
      const fromId = promotionRowKey(rr.id);
      const toId = serverIdOf.get(fromId);
      if (toId != null && fromId !== toId) idRemap.set(fromId, toId);
    }
    const commit = await sheet.applyPromotion(serverSheet, idRemap);
    // COW alignment witness — the load-bearing FE↔BE invariant of a
    // promote: the server MUST honor the client-minted sheet id (so the
    // handle's JS reference + every cached `ss_<hex>` key stay valid) and
    // the rebuilt handle MUST still carry it. The matcher only sees a
    // span's NAME, so the verdict rides in the LABEL (`-ok` vs
    // `-mismatch`) — a UX test asserts exactly one `cow-promote-ok` and
    // forbids `cow-promote-mismatch`. The data is for human triage. This
    // also makes the silent-promote bug class (a 422/empty-body or a
    // server-reassigned id that strands the ephemeral handle) observable
    // in session logs. See `ephemeral-promotion` in the sdk-2.0 docs.
    //
    // Canonicalize ALL THREE ids through `SheetId.of(...).toString()`
    // before comparing: the ephemeral handle id stringifies as a bare
    // dashed uuid (`6f08…-…`) while the server response id round-trips as
    // canonical `ss_<hex>`. Comparing raw strings would false-negative on a
    // correctly-honored id (same bytes, different surface form).
    const ephemeralId = SheetId.of(oldId).toString();
    const serverId = SheetId.of(String(serverSheet.id)).toString();
    const handleId = sheet.id.toString();
    const idHonored = serverId === ephemeralId;
    const handleStable = handleId === ephemeralId;
    Tracing.log(
      idHonored && handleStable ? "cow-promote-ok" : "cow-promote-mismatch",
      {
        ephemeralId,
        serverId,
        handleId,
        idHonored,
        handleStable,
        lookupKey: serialized.lookupKey ?? null,
        rowsSent: serialized.rows.length,
        serverRows: serverRows.length,
        rowsRekeyed: idRemap.size,
      },
    );
    try {
      seams.onSheetPromoted?.({
        sheetId: SheetId.of(handleId),
        previousSheetId: SheetId.of(ephemeralId),
        title: sheet.title,
        lookupKey: serialized.lookupKey ?? null,
        rowsSent: serialized.rows.length,
        rowsRekeyed: idRemap.size,
        commit,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      Tracing.warn("ui")(`onSheetPromoted callback failed: ${message}`);
    }
    sheet.promoted();
    seams.rehome(oldId, serialized.lookupKey ?? null);
  };
}
